import classNames from 'classnames';
import { isToday, isTomorrow } from 'date-fns';
import { useTranslation } from 'next-i18next';

import {
  isAvailable,
  isBlockchainAuctionRelease,
  isSoldOut,
  isTimedRelease,
} from '../../utils/product';
import ProductDate from '../Containers/ProductDate';
import { getTileStatus } from './utils/status';

type Props = {
  className?: string;
  product: Product;
  isPreOrder?: boolean;
};

const ProductTileStatus: React.FC<Props> = ({
  className = '',
  isPreOrder,
  product,
}) => {
  const timedRelease = isTimedRelease(product);
  const isBlockchainAuction = isBlockchainAuctionRelease(product);
  const { t } = useTranslation('product');

  if (isAvailable(product) && !timedRelease && !isBlockchainAuction)
    return null;

  const tileStatus = getTileStatus(product, isPreOrder);
  if (tileStatus === null) return null;

  let productDateStatus;

  if (isSoldOut(product)) {
    productDateStatus = <span className="text-neutral-4">Sold out</span>;
  } else if (tileStatus.active) {
    if (isPreOrder) {
      productDateStatus = (
        <ProductDate
          date={
            product.closingDate
              ? new Date(product.closingDate)
              : new Date(product.launchDate || '')
          }
          keyCountdownDate="countdown.releasing-preorder.date"
          keyCountdownDays="countdown.releasing-preorder.days"
          keyCountdownTime="countdown.releasing-preorder.time"
          uniqueDateFormat="dd MMMM"
        />
      );
    } else {
      let date = new Date();
      if (product.closingDate) {
        date = new Date(product.closingDate);
      } else if (product.launchDate) {
        date = new Date(product.launchDate);
      }

      if (isBlockchainAuction && (isToday(date) || isTomorrow(date))) {
        productDateStatus = isToday(date)
          ? t('auction.closingToday')
          : t('auction.closingTomorrow');
      } else {
        productDateStatus = (
          <ProductDate
            date={date}
            keyCountdownDate={
              timedRelease ? 'countdown.ending.date' : 'countdown.closing.date'
            }
            keyCountdownDays={
              timedRelease ? 'countdown.ending.days' : 'countdown.closing.days'
            }
            keyCountdownTime={
              timedRelease ? 'countdown.ending.time' : 'countdown.closing.time'
            }
            uniqueDateFormat="dd MMMM"
          />
        );
      }
    }
  } else {
    productDateStatus = (
      <ProductDate
        date={product.date ? new Date(product.date) : new Date()}
        keyCountdownDate={
          timedRelease || isBlockchainAuction
            ? 'countdown.starting.date'
            : 'countdown.releasing.date'
        }
        keyCountdownDays={
          timedRelease || isBlockchainAuction
            ? 'countdown.starting.days'
            : 'countdown.releasing.days'
        }
        keyCountdownTime={
          timedRelease || isBlockchainAuction
            ? 'countdown.starting.time'
            : 'countdown.releasing.time'
        }
        uniqueDateFormat="dd MMMM"
      />
    );
  }

  return (
    <div
      className={classNames(
        tileStatus.active ? 'text-active-4' : 'text-neutral-6',
        className,
      )}
    >
      {productDateStatus}
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default ProductTileStatus;
